/**
 * S22 — flow-view layout data. A view carrying a `flow <Ref>` clause renders
 * the referenced process. Mode dispatch (plan 2026-07-07-bpmn-lanes-pools P5):
 * plain → the swimlane `flow` variant (unchanged), `sequence` → the sequence
 * variant, `bpmn` → the horizontal BPMN layout with the view's lane/pool body
 * applied. Resolution (instances, knob bindings, `flow $target`) goes through
 * viewer-core's `resolveFlowViewByName` — the same helper the headless
 * MCP/server/CLI surfaces use, so a view renders identically live and
 * exported. Headless + pure; the container renders SwimlaneView or BpmnView
 * over the result.
 *
 * Returns null when the view has no `flow` clause or the ref doesn't resolve.
 */
import {
  buildNodeUniverse,
  resolveKnobBindings,
  walkerContextFor,
  swimlaneContextFor,
  walkProcess,
  swimlaneLayout,
  type BpmnLayout,
  type ResolvedModel,
  type ProcessModel,
  type SwimlaneLayout,
  type WalkResult,
  type WalkStep,
  type WalkerState,
  type ViewDeclV2,
} from "@archlang/engine";
import { bpmnLayoutForFlowView, resolveFlowProcess, resolveFlowViewByName } from "@archlang/viewer-core";
import { qualifiedViewName } from "./viewRepr.js";

/** The empty/static walker state (no branch picks, nothing expanded) — matches
 *  useProcessPlayback's `emptyWalkerState` fallback for a non-playing flow. */
export const STATIC_WALKER_STATE: WalkerState = {
  branchSelection: new Map(),
  expandedSubprocesses: new Set(),
};

/**
 * The synthesized process-tab view (VIEWS.md §intro): opening a process tab
 * renders `view { flow <That> }` — the exact analog of the board's synthesized
 * `view Default {}`. `handle` is the tab's `processKey`: a stable #id, a
 * fully-qualified name, or the content-keyed identity an ANONYMOUS bare-step
 * run is addressed through — all resolved by the same `resolveFlowProcess`
 * every flow surface uses.
 */
export function synthesizedFlowView(handle: string): ViewDeclV2 {
  return {
    name: "",
    header: { fields: [], knobs: [] },
    clauses: [{ kind: "flow", ref: { kind: "ref", path: [handle] } }],
  } as unknown as ViewDeclV2;
}

/** Resolve a `flow <Ref>` clause's target to a ProcessModel (qualified-suffix
 *  match; `$target` knobs resolve through the shared viewer-core helper when
 *  the view is registered in the model). Re-addressing rides the SPACE-AWARE
 *  `qualifiedViewName` (viewRepr) — a bare twin name would refuse as
 *  ambiguous (v0.11); the flow ref itself prefers the view's own space. */
export function flowProcessOf(view: ViewDeclV2, model: ResolvedModel): ProcessModel | null {
  const fc = view.clauses.find((c) => c.kind === "flow");
  if (!fc) return null;
  if (fc.ref.kind === "ref") {
    return resolveFlowProcess(model, fc.ref.path.join("."), view.ownerPath, view.space);
  }
  const r = resolveFlowViewByName(model, qualifiedViewName(view));
  return r.ok ? r.resolved.proc : null;
}

export type FlowData =
  | { readonly kind: "swimlane"; readonly layout: SwimlaneLayout; readonly walk: WalkResult }
  | {
      readonly kind: "bpmn";
      readonly layout: BpmnLayout;
      /** stepIndex → WalkStep, for the step→lane owner-reassign gesture (needs
       *  the step's nodeId → stepId + its callee `to`). Absent when the walk
       *  can't be built. */
      readonly stepByIndex: ReadonlyMap<number, WalkStep>;
      /** Owner-reassign resolver: stepIndex → hop stepId key the span-scoped
       *  `reassignStepOwner` mutation targets. Absent entry ⇒ owner not
       *  source-rewritable (the drop is shown disabled, never a silent no-op). */
      readonly reassignTargets: ReadonlyMap<number, string>;
      /** `#id` or qualified name of the flow's process — the mutateProcessStep
       *  target for a reassign. */
      readonly processHandle: string;
    };

/** Walk + lay out the flow-view's process per the clause's mode. null when no
 *  flow clause / unresolved ref. */
export function computeFlowData(view: ViewDeclV2, model: ResolvedModel): FlowData | null {
  const fc = view.clauses.find((c) => c.kind === "flow");
  if (!fc) return null;

  // Full resolution (instances, knobs, lane body) via the shared helper —
  // but ONLY for the view object actually registered in the model: a
  // synthesized/live-draft view that happens to SHARE a registered view's
  // name must render its OWN clauses, not the registered config (fleet
  // finding: name-based re-lookup). Drafts take the direct fallback below.
  const registered = model.views?.includes(view) ?? false;
  const named = registered
    ? resolveFlowViewByName(model, qualifiedViewName(view))
    : ({ ok: false } as const);
  const resolved = named.ok
    ? named.resolved
    : (() => {
        const proc = flowProcessOf(view, model);
        if (!proc) return null;
        // The draft's OWN knob defaults/bindings still resolve (a bpmn lane
        // selector may read `$knob`) — over the view object in hand, exactly
        // like the registry path does for registered views.
        const { knobs } = resolveKnobBindings(view, buildNodeUniverse(model));
        return { view, mode: fc.mode ?? "flow", proc, ...(fc.body ? { body: fc.body } : {}), knobs } as const;
      })();
  if (!resolved) return null;

  if (resolved.mode === "bpmn") {
    const { layout } = bpmnLayoutForFlowView(model, resolved);
    // A parallel walk (same static state + context bpmnLayoutForFlowView uses)
    // yields the per-step nodeId + callee + reassign resolver the owner-reassign
    // gesture needs; stepIndex aligns by construction (deterministic pre-order
    // DFS). `showAllBranches` MUST match the BPMN scaffold walk (freshBpmnScaffold)
    // — else nested branch steps get a different index than the rendered node.
    const walk = walkProcess(resolved.proc, STATIC_WALKER_STATE, walkerContextFor(model, resolved.proc), {
      showAllBranches: true,
    });
    const processHandle = resolved.proc.id
      ? `#${resolved.proc.id}`
      : [...(resolved.proc.ownerPath ?? []), resolved.proc.name].filter(Boolean).join(".");
    return { kind: "bpmn", layout, stepByIndex: walk.byIndex, reassignTargets: walk.reassignTargets, processHandle };
  }
  const walk = walkProcess(resolved.proc, STATIC_WALKER_STATE, walkerContextFor(model, resolved.proc));
  const layout = swimlaneLayout(walk, swimlaneContextFor(model), {
    variant: resolved.mode === "sequence" ? "sequence" : "flow",
  });
  return { kind: "swimlane", layout, walk };
}
